import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAtom } from 'jotai';
import { OutputSelector } from 'reselect';
import { NOC_MODE_FILTERS, DEFAULT_SORT } from '@utils/constants';
import useSupportedMetrics from '@hooks/metrics/useSupportedMetrics';
import useSupportedFlowMetrics from '@hooks/metrics/useSupportedFlowMetrics';
import { supportedMetricsSelector } from '@store/common/selectors';
import useGlobalExcludeMetricCategories from '@src/hooks/metrics/useGlobalExcludeMetricCategories';
import {
  nocModeSelector,
  trendsViewSelector,
  filtersViewSelector,
} from '@store/noc/base/selectors';
import { setFiltersSorting, setTrendsSorting } from '@store/noc/base/actions';
import MetricSelectorPopup from '@components/common/MetricSelectorPopup';
import { filtersProps, trendsProps } from './utils';
import { getLimitationByUsecaseKey } from '@src/hooks/useCurrentLimitation';
import { currentKpiIdAtom } from '@src/atoms/panels';

interface MDProps {
  metricSelector: OutputSelector<any, any[], (res1: {}, res2: any) => any[]>;
  setMetrics: (metrics: string[]) => any;
}

// wrapper to switch between filters and trends context
const Wrapper = () => {
  const mode = useSelector(nocModeSelector);
  const dropdownProps = mode === NOC_MODE_FILTERS ? filtersProps : trendsProps;

  return <MetricDropdown {...dropdownProps} />;
};

function MetricDropdown({ metricSelector, setMetrics }: MDProps) {
  const dispatch = useDispatch();
  const mode = useSelector(nocModeSelector);
  const supportedMetrics: Record<string, any> = useSelector(supportedMetricsSelector);
  const trends = useSelector(trendsViewSelector);
  const globalExcludeMetricsCategory = useGlobalExcludeMetricCategories([]);
  const filters = useSelector(filtersViewSelector);
  const limitation = getLimitationByUsecaseKey('realtime');
  const [currentKpiId, setCurrentKpiId] = useAtom(currentKpiIdAtom);

  const onMetricSelectHandler = (metrics) => {
    mode === NOC_MODE_FILTERS ? setFilterSort(metrics) : setTrendsSort(metrics);
    dispatch(setMetrics(metrics));
  };

  const setTrendsSort = (metrics) => {
    if (!metrics.includes(trends.sort.key)) {
      dispatch(setTrendsSorting(DEFAULT_SORT));
    }
  };

  const setFilterSort = (metrics) => {
    if (!metrics.includes(filters.sort.key)) {
      dispatch(setFiltersSorting(DEFAULT_SORT));
    }
  };

  const metrics = useSupportedMetrics({
    excludeCategories: globalExcludeMetricsCategory,
    limitation,
    allMetrics: supportedMetrics,
  });

  const flowData = useSupportedFlowMetrics({
    allMetrics: supportedMetrics,
  });

  const { selectedMetrics }: any = useSelector(metricSelector);

  return (
    <MetricSelectorPopup
      supportedMetrics={metrics}
      flowData={flowData}
      selectedMetrics={selectedMetrics}
      onApply={onMetricSelectHandler}
      config={limitation?.metrics?.config}
      currentKpiId={currentKpiId}
      onKpiChange={setCurrentKpiId}
    />
  );
}

export default Wrapper;
